import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "@styles/pages/Redeem/step2/RedeemSamsungRewardPage.scss";
import { getOfferDetail, submitConvertRewards } from "@api/redeemApi";
import { getMemberProfile } from "@api/memberApi";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Slider,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { setFormStep } from "@store/ui/formStepSlice";
import { setRedeemGiftDetailData } from "@store/redeem/redeemSlice";
import { setMemberInfoData } from "@store/member/memberInfoSlice";
import useScrollToTop from "@hooks/useScrollToTop";
import AccordionRow from "@components/ui/AccordionRow";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import Popup from "reactjs-popup";
import { toast } from "react-toastify";
import VideoPlayer from "@components/ui/VideoPlayer";

const RedeemSamsungRewardPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  useScrollToTop();
  const isChinese = i18n.language === "hk";
  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );
  const redeemDetail = useSelector(
    (state) => state.redeemGiftDetail.redeemGiftDetailData
  );
  const tncList = t("redeem:tnc_list", { returnObjects: true });

  const UNIT_POINT = 2500; // 1 件 = 2000 分，可以之後改
  const [quantity, setQuantity] = useState("");
  const [amount, setAmount] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [finalCount, setFinalCount] = useState(0); // 最終要用幾多分
  const [hasError, setHasError] = useState(false); // ✅ 只用 boolean
  const [isLoading, setIsLoading] = useState(false);
  const [tncText, setTncText] = useState({ hk: "", hk_en: "" });
  const [openPopup, setOpenPopup] = useState(false);
  const [openVideoPopup, setOpenVideoPopup] = useState(false);
  const [redirectToLinkUpProgress, setRedirectToLinkUpProgress] = useState(0);
  const redirectURL = isChinese
    ? "https://www.samsung.com/hk/mypage/rewards/"
    : "https://www.samsung.com/hk_en/mypage/rewards/";
  const redirectedRef = useRef(false);

  useEffect(() => {
    const q = Number(quantity) || 0;
    setFinalCount(q); // 最終都以 TextField (quantity) 為主
  }, [quantity]);

  const calculateTotalPoint = () => {
    const point = Number(redeemDetail?.point_required) || 0;
    const qty = Number(quantity) || 0;
    return point * qty;
  };

  const totalPoint = calculateTotalPoint();
  useEffect(() => {
    if (!openVideoPopup) return;

    // 打開 popup 先重置
    setRedirectToLinkUpProgress(0);
    redirectedRef.current = false;

    const timer = setInterval(() => {
      setRedirectToLinkUpProgress((old) => {
        const next = Math.min(old + Math.random() * 5, 100);

        if (next === 100 && !redirectedRef.current) {
          redirectedRef.current = true;
          window.location.href = redirectURL;
        }
        return next;
      });
    }, 100);

    return () => {
      clearInterval(timer);
      setRedirectToLinkUpProgress(0);
      redirectedRef.current = false;
    };
  }, [openVideoPopup]);

  const qtyRef = useRef(null);

  // ✅ 驗證：必填 & > 1（回傳 boolean）
  const isQtyInvalid = (raw) => {
    if (raw === "" || raw === null) return true;
    const n = Number(raw);
    if (!Number.isFinite(n)) return true;
    if (n <= 0) return true;
    return false;
  };

  const handleChange = (e) => {
    // 只保留數字（user 想清除就會變空字串）
    const next = e.target.value.replace(/[^0-9]/g, "");
    setQuantity(next);
    setHasError(isQtyInvalid(next));

    // 只更新最後分數，唔郁 amount
    setFinalCount(Number(next) || 0);
  };

  const handleChangeAmount = (delta) => {
    setAmount((prev) => {
      const nextAmount = Math.max(prev + delta, 0); // 最少 0 件

      // 根據件數計分數，直接 overwrite TextField
      const nextQuantity =
        nextAmount === 0 ? "" : String(nextAmount * UNIT_POINT);

      setQuantity(nextQuantity);
      setHasError(isQtyInvalid(nextQuantity));
      setFinalCount(Number(nextQuantity) || 0);

      return nextAmount;
    });
  };

  const handleBlur = () => {
    setHasError(isQtyInvalid(quantity));
  };

  // ⬇️ 按 Convert 前先檢查；通過才開 Popup
  const handleOpenPopupClick = () => {
    const isSliderType = redeemDetail.redeem_type === 7;
    const isQtyType = redeemDetail.redeem_type === 9;

    // ✅ Type 9：用 quantity 驗證（保持你原本邏輯）
    if (isQtyType) {
      const invalid = isQtyInvalid(quantity);
      setHasError(invalid);
      if (invalid) {
        qtyRef.current?.focus();
        qtyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }

    // ✅ Type 7：用 slider 驗證（簡單啲：唔可以 0）
    if (isSliderType) {
      if (sliderValue <= 0) {
        toast.error(t("redeem:please_use_slider")); // 你可以換做 i18n key
        return;
      }
    }

    // 兩邊都 OK 先開 Popup
    setOpenPopup(true);
  };

  const decodeHTMLWithLineBreak = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerText.trim();
  };

  useEffect(() => {
    const fetchOfferDetail = async () => {
      try {
        const res = await getOfferDetail({ redeem_id: redeemDetail.id });
        const data = res.data.Data;

        const mergedData = {
          ...redeemDetail,
          ...data.inhand_qty,
        };

        dispatch(setRedeemGiftDetailData(mergedData));
        setTncText({
          hk: decodeHTMLWithLineBreak(redeemDetail.desc_hk),
          hk_en: decodeHTMLWithLineBreak(redeemDetail.desc_hk_en),
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchOfferDetail();
  }, []);

  const onSubmitData = () => {
    const isSliderType = redeemDetail.redeem_type === 7;

    const finalData = {
      redeem_item_id: redeemDetail.id,
      convert_point: isSliderType
        ? sliderValue * 100 // 👈 你講的 rule：最後 slider * 100
        : Number(finalCount), // 其他 type（例如 9）照用原本邏輯
    };

    const postConvertRewards = async () => {
      setIsLoading(true);
      try {
        const res = await submitConvertRewards(finalData);
        const statusCode = res.data.StatusCode;

        switch (statusCode) {
          case 200:
            const profileRes = await getMemberProfile();
            dispatch(setMemberInfoData(profileRes.data.Data));
            toast.success(t("status_message:status_code_200_redeem_offer"));
            dispatch(setFormStep({ formKey: "redeem", step: 4 }));
            break;
          case 301:
            toast.error(t("status_message:status_code_301"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 302:
            toast.error(t("status_message:status_code_302"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 500:
            toast.error(t("status_message:status_code_500"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 601:
            toast.error(t("status_message:status_code_601"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 602:
            toast.error(t("status_message:status_code_602"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 603:
            toast.error(t("status_message:status_code_603"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 604:
            toast.error(t("status_message:status_code_604"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 606:
            toast.error(t("status_message:status_code_606"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 607:
            setOpenVideoPopup(true);
            setOpenPopup(false);
            break;
          case 610:
            console.log(610);
            toast.error(t("not linked sa account"));
            break;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    postConvertRewards();
  };
  return (
    <Box className="redeem-samsung-reward-page-container">
      <Box className="detail-container">
        <Box className="image-container">
          <img className="redeem-image" src={redeemDetail?.image} />
        </Box>
        <Box className="gift-container">
          <Box className="gift-detail-container">
            <Typography className="gift-name">
              {isChinese ? redeemDetail?.title_hk : redeemDetail?.title_hk_en}
            </Typography>
          </Box>
          {redeemDetail.redeem_type === 7 && (
            <Box className="redeem-slider-container">
              <Typography className="slider-info-text">
                <Trans i18nKey="redeem:redeem_info" />
              </Typography>
              <Box className="slider-container">
                <Slider
                  value={sliderValue}
                  onChange={(e, value) => {
                    // value 可能係 number 或 array（range 時），但你係單一 slider
                    setSliderValue(Number(value) || 0);
                  }}
                  min={0}
                  max={memberInfoData?.reward_limit || 0} // 👈 呢度直接用原本分數上限
                  step={1}
                  aria-label="Default"
                  valueLabelFormat={(value) => `$${value}`}
                  valueLabelDisplay="on"
                  sx={{
                    color: "#f5b623", // 主色（thumb）
                    height: 8,
                    width: "100%",
                    "& .MuiSlider-thumb": {
                      height: 24,
                      width: 24,
                      backgroundColor: "#fff",
                      border: "#f5b623 2px solid",
                      "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                        boxShadow: "inherit",
                      },
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: "#f5b623",
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#ffd777", // 可選：未填充部分
                    },

                    "& .MuiSlider-valueLabel": {
                      lineHeight: 1.2,
                      fontSize: 14,
                      background: "unset",
                      padding: 0,
                      width: 42,
                      height: 42,
                      borderRadius: "50% 50% 50% 0",
                      backgroundColor: "#f5b623",
                      transformOrigin: "bottom left",
                      transform:
                        "translate(50%, -100%) rotate(-45deg) scale(0)",
                      "&::before": { display: "none" },
                      "&.MuiSlider-valueLabelOpen": {
                        transform:
                          "translate(50%, -100%) rotate(-45deg) scale(1)",
                      },
                      "& > *": {
                        transform: "rotate(45deg)",
                      },
                    },
                  }}
                />
              </Box>
        
              <Box className="slider-info-container">
              <Typography>
                {(sliderValue * 100).toLocaleString()}{" "}
                <Trans i18nKey="redeem:redeem_s_dollar" />
              </Typography>
                <Box className="slider-equal">=</Box>
                <Typography>
                  {" "}
                  <Trans
                    i18nKey="redeem:redeem_samsung_rewards"
                    values={{
                      samsung_reward: (sliderValue * 100).toLocaleString(),
                    }}
                  /><br></br>
                  <Trans
                    i18nKey="redeem:redeem_hong_kong"
                    values={{
                      hkd: sliderValue,
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
          {redeemDetail.redeem_type === 9 && (
            <Box>
              <Box className="convert_info">
                <Typography>
                  <Trans i18nKey="redeem:convert_info" />
                </Typography>
              </Box>
              <Box className="convert_info">
                <Typography>
                  <Trans i18nKey="redeem:use_points" />
                  2,500 <Trans i18nKey="redeem:s_dollars" />
                </Typography>
              </Box>
              <Typography className="quantity-container">
                <Typography className="quantity-text">
                  <Trans i18nKey="redeem:quantity" />
                </Typography>
                <RemoveCircleIcon
                  className="quantity-icon"
                  onClick={() => handleChangeAmount(-1)}
                />
                <Typography className="quantity-text">{amount}</Typography>
                <AddCircleIcon
                  className="quantity-icon"
                  onClick={() => handleChangeAmount(1)}
                />
              </Typography>
            </Box>
          )}
          {redeemDetail.redeem_type === 9 && (
            <Typography className="quantity-container">
              <Typography className="quantity-text">
                <Trans i18nKey="redeem:quantity_transfer_1" />
              </Typography>
              <span>
                <TextField
                  type="number"
                  value={quantity}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={hasError}
                  inputProps={{
                    min: 1,
                    step: 1,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    onKeyDown: (e) => {
                      const blockKeys = ["e", "E", "+", "-", "."];
                      if (blockKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    },
                    onWheel: (e) => e.currentTarget.blur(),
                  }}
                  inputRef={qtyRef}
                />
                <Typography className="quantity-error">
                  {hasError && <Trans i18nKey="redeem:please_fill_quantity" />}
                </Typography>
              </span>
              <Typography className="quantity-text">
                <Trans i18nKey="redeem:quantity_transfer_2" />
              </Typography>
            </Typography>
          )}
          <Box className="button-container">
            <LoadingButton
              disableElevation
              disableRipple
              className="button"
              onClick={() =>
                dispatch(setFormStep({ formKey: "redeem", step: 0 }))
              }
              disabled={isLoading}
              loading={isLoading}
            >
              <Trans i18nKey="redeem:back" />
            </LoadingButton>

            {/* ✅ 這裡改：先驗證，通過才 setOpenPopup(true) */}
            <LoadingButton
              onClick={handleOpenPopupClick}
              className="loading-button"
              disabled={isLoading}
              loading={isLoading}
            >
              <Trans i18nKey="redeem:convert" />
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      <Box className="redemption-policy-container">
        <AccordionRow
          summaryTitle={<Trans i18nKey="redeem:redemption_policy" />}
          details={[
            {
              value: (
                <Box className="tnc">
                  {/* {tncList.map((text, idx) => (
                    <Box key={idx}>{text}</Box>
                  ))} */}
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: isChinese ? tncText.hk : tncText.hk_en,
                    }}
                  />
                </Box>
              ),
            },
          ]}
        />
      </Box>

      {/* Popup 視窗 */}
      <Popup open={openPopup} modal onClose={() => setOpenPopup(false)}>
        {(close) => (
          <Box className="popup-container">
            <Typography
              className="popup-title"
              dangerouslySetInnerHTML={{
                __html: t("redeem:confirm_to_convert", {
                  rewards: (redeemDetail.redeem_type === 7
                    ? sliderValue * 100 // 👈 Slider 類型：slider * 100
                    : Number(quantity) || 0
                  ) // 👈 Quantity 類型
                    .toLocaleString(),
                }),
              }}
            />
            <Box className="popup-button-container">
              <LoadingButton
                className="button"
                onClick={() => {
                  setOpenPopup(false);
                  close();
                }}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="form:no" />
              </LoadingButton>
              <LoadingButton
                className="loading-button"
                onClick={() => onSubmitData()}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="inquiry:yes" />
              </LoadingButton>
            </Box>
          </Box>
        )}
      </Popup>
      {openVideoPopup && (
        <Popup open={openVideoPopup} onClose={() => setOpenVideoPopup(false)}>
          <CancelIcon
            className="close-icon"
            onClick={() => {
              setOpenVideoPopup(false);
            }}
          />
          <Box className="notice-popup-container">
            <Box className="notice-icon-container">
              <img
                className="notice-logo"
                src={`${process.env.PUBLIC_URL}/assets/images/global/mobile_logo.png`}
                alt="logo"
              />
            </Box>
            <Typography className="notice-title-2">
              <Trans i18nKey="popup:redeem_after_login_and_linked_with_sa_rewards_tnc_consent" />
              <br /> <br />
              <LinearProgress
                variant="determinate"
                value={redirectToLinkUpProgress}
                className="custom-progress"
              />
            </Typography>

            <Typography className="notice-title-b ">
              <Trans i18nKey="popup:tutorial_video" />
            </Typography>
            <VideoPlayer
              videoUrl={
                isChinese
                  ? "https://hkclubs.samsung.com/Media/Video/activiate_rewards_TC.mp4"
                  : "https://hkclubs.samsung.com/Media/Video/activiate_rewards_EN.mp4"
              }
            />
          </Box>
        </Popup>
      )}
    </Box>
  );
};

export default RedeemSamsungRewardPage;
